import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, X, UserCheck, CalendarClock, Ban, RotateCcw, ClipboardCheck } from "lucide-react";
import { interviewSchedulingApi } from "../../services/interviewSchedulingApi";
import { formatApiErrorDetail } from "../../lib/api";
import { usePermissions } from "../../hooks/usePermissions";
import { BookingCard } from "../../components/recruitment/BookingCard";
import { BookingActionsModal } from "../../components/recruitment/BookingActionsModal";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

const STATUS_FILTERS = ["ALL", "PENDING_CONFIRMATION", "CONFIRMED", "HELD", "RESCHEDULE_REQUESTED", "CANCELLED_BY_APPLICANT", "CANCELLED_BY_MANAGEMENT", "COMPLETED", "NO_SHOW"];

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Monday start
  d.setDate(d.getDate() + diff);
  return d;
}

export default function InterviewCalendarPage() {
  const { t } = useLocalization();
  const { canManageInterviews } = usePermissions();
  const [view, setView] = useState("agenda"); // agenda | week
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [activeBooking, setActiveBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = statusFilter !== "ALL" ? { status: statusFilter } : {};
    interviewSchedulingApi.listBookings(params).then((r) => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!canManageInterviews) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageInterviews, statusFilter]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  }), [weekStart]);

  const bookingsByDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.date) continue;
      (map[b.date] = map[b.date] || []).push(b);
    }
    return map;
  }, [bookings]);

  if (!canManageInterviews) return <p className="text-sm text-gray-500" data-testid="interview-calendar-access-denied">{t("common.accessDenied")}</p>;

  const runAction = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      setActiveBooking(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="interview-calendar-page">
      <PageHelpBanner pageKey="interview-calendar" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtRecruitment.calendarHelp")}</p>
      </PageHelpBanner>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtRecruitment.calendarTitle")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtRecruitment.calendarSubtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button data-testid="calendar-view-agenda" onClick={() => setView("agenda")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${view === "agenda" ? "bg-white shadow text-blue-950" : "text-gray-500"}`}>{t("mgmtRecruitment.agenda")}</button>
            <button data-testid="calendar-view-week" onClick={() => setView("week")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${view === "week" ? "bg-white shadow text-blue-950" : "text-gray-500"}`}>{t("mgmtRecruitment.week")}</button>
          </div>
          <select data-testid="calendar-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-xs">
            {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
          </select>
        </div>
      </div>

      {view === "week" && (
        <div className="flex items-center justify-between">
          <button data-testid="calendar-week-prev" onClick={() => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronLeft size={16} /></button>
          <p className="text-sm font-semibold text-gray-700">{weekDays[0]} - {weekDays[6]}</p>
          <button data-testid="calendar-week-next" onClick={() => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><ChevronRight size={16} /></button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500" data-testid="interview-calendar-loading">{t("common.loading")}</p>
      ) : view === "week" ? (
        <div className="overflow-x-auto">
          <div className="grid grid-cols-7 gap-2 min-w-[700px]" data-testid="calendar-week-grid">
            {weekDays.map((date) => (
              <div key={date} className="bg-white rounded-xl border border-gray-200 p-2 min-h-[140px]" data-testid={`calendar-day-${date}`}>
                <p className="text-xs font-bold text-gray-500 mb-2">{date.slice(5)}</p>
                <div className="space-y-1.5">
                  {(bookingsByDate[date] || []).map((b) => (
                    <BookingCard key={b.id} booking={b} compact onClick={() => setActiveBooking(b)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="calendar-agenda-list">
          {Object.keys(bookingsByDate).sort().length === 0 && (
            <p className="text-sm text-gray-500 py-8 text-center" data-testid="calendar-agenda-empty">{t("mgmtRecruitment.noBookingsMatchFilter")}</p>
          )}
          {Object.keys(bookingsByDate).sort().map((date) => (
            <div key={date} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`agenda-group-${date}`}>
              <p className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-3">{date}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {bookingsByDate[date].map((b) => (
                  <BookingCard key={b.id} booking={b} onClick={() => setActiveBooking(b)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeBooking && (
        <BookingActionsModal
          booking={activeBooking}
          onClose={() => setActiveBooking(null)}
          onConfirm={() => runAction(() => interviewSchedulingApi.confirmBooking(activeBooking.id), t("mgmtRecruitment.interviewConfirmed"))}
          onCancel={(reason) => runAction(() => interviewSchedulingApi.cancelBooking(activeBooking.id, reason), t("mgmtRecruitment.interviewCancelled"))}
          onReschedule={(date, startTime) => runAction(() => interviewSchedulingApi.rescheduleBooking(activeBooking.id, { date, start_time: startTime }), t("mgmtRecruitment.interviewRescheduled"))}
          onProposeTime={(date, startTime) => runAction(() => interviewSchedulingApi.proposeTime(activeBooking.id, { date, start_time: startTime }), t("mgmtRecruitment.newTimeProposedShort"))}
          onAssignInterviewer={(name) => runAction(() => interviewSchedulingApi.assignInterviewer(activeBooking.id, name), t("mgmtRecruitment.interviewerAssigned"))}
          onComplete={() => runAction(() => interviewSchedulingApi.markCompleted(activeBooking.id), t("mgmtRecruitment.markedCompleted"))}
          onNoShow={() => runAction(() => interviewSchedulingApi.markNoShow(activeBooking.id), t("mgmtRecruitment.markedNoShow"))}
        />
      )}
    </div>
  );
}
